import clsx from 'clsx';
import { type RefObject, useContext, useMemo } from 'react';

import { crosshair } from './XYChartCrosshair.sty.js';
import { plotLayout } from '../../../../core/components/chart-grid/ChartGrid.sty.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { HorizontalCrosshair } from '../../../../core/components/crosshair/HorizontalCrosshair.js';
import { VerticalCrosshair } from '../../../../core/components/crosshair/VerticalCrosshair.js';
import {
  useSelector,
  useTooltipSnapshot,
} from '../../../../core/components/overlay-tooltip/selectors.js';
import { useTooltipReducer } from '../../../../core/components/overlay-tooltip/useTooltipReducer.js';
import { ChartVariantContext } from '../../../../core/context/ChartVariantContext.js';
import { useApplyCallbackOnScroll } from '../../../../core/hooks/useApplyCallbackOnScroll.js';
import { useAxesFormatter } from '../../../hooks/useAxesFormatter.js';
import { useFormatCategory } from '../../../hooks/useFormatCategory.js';
import { useGetThresholds } from '../../../hooks/useGetThresholds.js';
import { useVisibleAxesConfig } from '../../../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../../../hooks/useVisibleAxesScales.js';
import { useXYChartCrosshairHidden } from '../../../hooks/useXYChartCrosshairHidden.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import { useFilteredSeriesIdsState } from '../../../store/selectors.js';
import {
  UndefinedContent,
  type XYChartTooltipMetadata,
} from '../../../types/state.js';
import {
  isAreaTooltipContent,
  isDotTooltipContent,
  isLineTooltipContent,
} from '../../../utils/type-guards.js';
import { relAccessor } from '../../../utils/value-representation.js';
import { calculateCrosshairCoordinates } from '../utils/calculate-crosshair-coordinates.js';
import {
  chartCrosshairDataTransformer,
  type CrosshairLabelsGetter,
} from '../utils/chartCrosshairDataTransformer.js';

type XYChartCrosshairLinesProps = Readonly<{
  getLabels: CrosshairLabelsGetter;
  height: number;
  thresholdOffset: ReturnType<typeof useGetThresholds>['offset'];
  thresholdYAxes: ReturnType<typeof useGetThresholds>['yAxes'];
  axisIdToScaleDomain: ReturnType<typeof useXYChartData>['axisIdToScaleDomain'];
  seriesToMetadata: ReturnType<typeof useXYChartData>['seriesToMetadata'];
  datapointMetadataMap: ReturnType<
    typeof useXYChartData
  >['datapointMetadataMap'];
  axisIdToScales: ReturnType<typeof useXYChartScales>;
  hasVisibleSeriesOnThresholdAxis: (axisIds: Set<string>) => boolean;
  chartBounds: DOMRect | undefined;
  isBottomLabelRotated: boolean;
  isTopLabelRotated: boolean;
  widthWithBaseOffset: number;
  hideHorizontal: boolean;
  hideVertical: boolean;
}>;

function XYChartCrosshairLines({
  getLabels,
  height,
  thresholdOffset,
  thresholdYAxes,
  axisIdToScaleDomain,
  seriesToMetadata,
  datapointMetadataMap,
  axisIdToScales,
  hasVisibleSeriesOnThresholdAxis,
  chartBounds,
  isBottomLabelRotated,
  isTopLabelRotated,
  widthWithBaseOffset,
  hideHorizontal,
  hideVertical,
}: XYChartCrosshairLinesProps) {
  useSelector(({ relativeX, relativeY }) => `${relativeX}-${relativeY}`);
  const { relativeX, relativeY, pinned, data } = useTooltipSnapshot();
  const position =
    relativeX !== null && relativeY !== null
      ? { x: relativeX, y: relativeY }
      : undefined;

  const tooltipContent =
    (data.metadata as XYChartTooltipMetadata | undefined)?.preferred ??
    UndefinedContent;

  const { xAxis, yAxis, variant, orientation, isStacked } =
    seriesToMetadata.get(tooltipContent?.series) ?? {};

  const isArea = isAreaTooltipContent(tooltipContent);
  const isHorizontal = orientation === 'horizontal';

  const calculatedCoordinates = useMemo(() => {
    const tooltipContentHasCenteredCrosshair =
      isDotTooltipContent(tooltipContent) ||
      isLineTooltipContent(tooltipContent) ||
      isAreaTooltipContent(tooltipContent);

    if (
      !pinned ||
      !tooltipContentHasCenteredCrosshair ||
      !tooltipContent.series ||
      !tooltipContent.datapoint
    ) {
      return null;
    }

    const xRelAccessor = xAxis
      ? relAccessor(xAxis, axisIdToScaleDomain)
      : undefined;
    const yRelAccessor = yAxis
      ? relAccessor(yAxis, axisIdToScaleDomain)
      : undefined;

    return calculateCrosshairCoordinates({
      pinned,
      tooltipContentHasCenteredCrosshair,
      tooltipContentSeries: tooltipContent.series,
      datapoint: tooltipContent.datapoint,
      axisIdToScales,
      datapointMetadataMap,
      xRelAccessor,
      yRelAccessor,
      isArea,
      isHorizontal,
      isStacked,
    });
  }, [
    tooltipContent,
    xAxis,
    yAxis,
    axisIdToScaleDomain,
    pinned,
    axisIdToScales,
    datapointMetadataMap,
    isArea,
    isHorizontal,
    isStacked,
  ]);

  const { crosshair: crosshairConfig } = useContext(ChartVariantContext);

  const coordinates = calculatedCoordinates ?? position ?? { x: 0, y: 0 };

  if (!position) {
    return null;
  }

  const labels = getLabels({ position, pinned });

  const leftOffset =
    labels.left && hasVisibleSeriesOnThresholdAxis(thresholdYAxes.left)
      ? thresholdOffset.left
      : 0;
  const rightOffset =
    labels.right && hasVisibleSeriesOnThresholdAxis(thresholdYAxes.right)
      ? thresholdOffset.right
      : 0;
  const widthWithOffset = widthWithBaseOffset + leftOffset + rightOffset;

  const isBand = variant === 'band';
  const showHorizontalCrosshair =
    (!isBand || isHorizontal || !pinned) &&
    !crosshairConfig?.horizontalHidden &&
    !hideHorizontal;
  const showVerticalCrosshair =
    (!isBand || !isHorizontal || !pinned) &&
    !crosshairConfig?.verticalHidden &&
    !hideVertical;

  return (
    <svg className={clsx(plotLayout, crosshair)}>
      {showHorizontalCrosshair && (
        <HorizontalCrosshair
          dashed={!pinned}
          leftLabel={labels.left}
          rightLabel={labels.right}
          y={coordinates.y}
          width={widthWithOffset}
          height={height}
          chartBounds={chartBounds}
          leftOffset={leftOffset}
          rightOffset={rightOffset}
        />
      )}
      {showVerticalCrosshair && (
        <VerticalCrosshair
          dashed={!pinned}
          bottomLabel={labels.bottom}
          topLabel={labels.top}
          isBottomLabelRotated={isBottomLabelRotated}
          isTopLabelRotated={isTopLabelRotated}
          x={coordinates.x}
          height={height}
          width={widthWithOffset}
          chartBounds={chartBounds}
        />
      )}
    </svg>
  );
}

export function XYChartCrosshair({
  chartRef,
}: Readonly<{
  chartRef: RefObject<HTMLDivElement | null>;
}>) {
  const filteredSeriesIds = useFilteredSeriesIdsState();
  const axesScale = useVisibleAxesScales();
  const axesConfig = useVisibleAxesConfig();
  const axesIdToFormatter = useAxesFormatter();
  const formatCategory = useFormatCategory();
  const { width, height } = useGridPlotSize();
  const { offset: thresholdOffset, yAxes: thresholdYAxes } = useGetThresholds();
  const { axisIdToScaleDomain, seriesToMetadata, datapointMetadataMap } =
    useXYChartData();
  const axisIdToScales = useXYChartScales();

  const chartBounds = chartRef.current?.getBoundingClientRect();

  const dispatch = useTooltipReducer();
  const pinned = useSelector(({ pinned }) => pinned);
  useApplyCallbackOnScroll(pinned, dispatch({ type: 'RESET_TOOLTIP' }));

  const isBottomLabelRotated =
    axesConfig['bottom']?.tickLabelLayout === 'vertical';
  const isTopLabelRotated = axesConfig['top']?.tickLabelLayout === 'vertical';

  const { hideHorizontal, hideVertical } = useXYChartCrosshairHidden();

  const hasVisibleSeriesOnThresholdAxis = (() => {
    if (!filteredSeriesIds) {
      return (_axisIds: Set<string>) => true;
    }
    const visibleSeriesEntries = [...seriesToMetadata.entries()].filter(
      ([series]) => filteredSeriesIds.has(series.id),
    );
    return (axisIds: Set<string>) => {
      return visibleSeriesEntries.some(([, series]) =>
        axisIds.has(series.yAxis?.id ?? ''),
      );
    };
  })();

  const getLabels = chartCrosshairDataTransformer({
    axesScale,
    axesConfig,
    axesIdToFormatter,
    width,
    formatCategory,
  });

  return (
    <XYChartCrosshairLines
      getLabels={getLabels}
      height={height}
      thresholdOffset={thresholdOffset}
      thresholdYAxes={thresholdYAxes}
      axisIdToScaleDomain={axisIdToScaleDomain}
      seriesToMetadata={seriesToMetadata}
      datapointMetadataMap={datapointMetadataMap}
      axisIdToScales={axisIdToScales}
      hasVisibleSeriesOnThresholdAxis={hasVisibleSeriesOnThresholdAxis}
      chartBounds={chartBounds}
      isBottomLabelRotated={isBottomLabelRotated}
      isTopLabelRotated={isTopLabelRotated}
      widthWithBaseOffset={width}
      hideHorizontal={hideHorizontal}
      hideVertical={hideVertical}
    />
  );
}
